import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Sparkles } from "lucide-react";
import { useGame } from "@/context/GameContext";
import { Sound } from "@/lib/sound";

const LearningTip = ({ tip, open, onClose }) => {
  const { markTipSeen } = useGame();
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  if (!tip) return null;

  const handleSubmit = () => {
    if (selected == null) return;
    const isCorrect = selected === tip.quiz.answer;
    setSubmitted(true);
    if (isCorrect) Sound.success(); else Sound.error();
    markTipSeen(tip.id, isCorrect);
  };

  const handleClose = () => {
    setSelected(null);
    setSubmitted(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        className="max-w-md rounded-3xl border-4 border-amber-300 bg-white p-0 overflow-hidden"
        data-testid="learning-tip-modal"
      >
        <div className="bg-gradient-to-br from-amber-300 to-amber-400 p-5 border-b-4 border-amber-500">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="text-5xl animate-bounce-slow">{tip.icon}</div>
              <div>
                <div className="text-[11px] uppercase tracking-widest font-black text-amber-900">
                  Business Tip · {tip.topic}
                </div>
                <DialogTitle className="font-display text-2xl font-bold text-slate-900">
                  {tip.title}
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-slate-700 leading-relaxed font-medium">{tip.body}</p>

          <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-black uppercase tracking-wider text-blue-700">
                Quiz — win $5!
              </span>
            </div>
            <p className="font-heading font-bold text-slate-800 mb-3">{tip.quiz.q}</p>
            <div className="grid gap-2">
              {tip.quiz.options.map((opt, i) => {
                const isSelected = selected === i;
                const isCorrect = submitted && i === tip.quiz.answer;
                const isWrong = submitted && isSelected && i !== tip.quiz.answer;
                return (
                  <button
                    key={opt}
                    disabled={submitted}
                    onClick={() => setSelected(i)}
                    data-testid={`tip-quiz-option-${i}`}
                    className={`text-left px-4 py-2.5 rounded-xl border-2 font-heading font-semibold transition-all ${
                      isCorrect
                        ? "bg-emerald-100 border-emerald-400 text-emerald-800"
                        : isWrong
                        ? "bg-rose-100 border-rose-400 text-rose-800"
                        : isSelected
                        ? "bg-blue-100 border-blue-400 text-blue-800"
                        : "bg-white border-slate-200 hover:border-blue-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{opt}</span>
                      {isCorrect && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                      {isWrong && <XCircle className="w-5 h-5 text-rose-600" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {!submitted ? (
            <Button
              onClick={handleSubmit}
              disabled={selected == null}
              data-testid="tip-quiz-submit"
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-black font-heading text-base py-6 rounded-2xl btn-3d border-2 border-blue-600 disabled:opacity-50"
            >
              Check my answer
            </Button>
          ) : (
            <Button
              onClick={handleClose}
              data-testid="tip-close-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-black font-heading text-base py-6 rounded-2xl btn-3d border-2 border-emerald-600"
            >
              {selected === tip.quiz.answer ? "Nice! Collect $5 →" : "Got it →"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LearningTip;
